import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD  = '#B8892A';
const BD    = '#E8E0D0';
const RED   = '#8C1F1F';
const MUTED = '#6B5E4E';
const GREEN = '#2A6645';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // step: 'loading' | 'invalid' | 'sms' | 'done'
  const [step, setStep]           = useState('loading');
  const [maskedPhone, setMasked]  = useState('');
  const [smsCode, setSmsCode]     = useState('');
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirm]  = useState('');
  const [smsSent, setSmsSent]     = useState(false);
  const [smsCooldown, setCooldown]= useState(0);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [invalidReason, setInvalidReason] = useState('');

  const inputStyle = {
    width: '100%', padding: '11px 14px', border: '1px solid ' + BD, borderRadius: 2,
    fontFamily: 'var(--sans)', fontSize: 13, color: '#110E0A',
    background: '#F8F5F0', outline: 'none', boxSizing: 'border-box',
  };

  // On mount: validate token and trigger SMS
  useEffect(() => {
    if (!token) { setInvalidReason('重置链接无效，请重新申请'); setStep('invalid'); return; }
    api.post('/auth/reset-password/send-sms', { token })
      .then(res => {
        setMasked(res.data.masked_phone || '');
        setSmsSent(true);
        setStep('sms');
        startCooldown();
      })
      .catch(err => {
        setInvalidReason(err.response?.data?.error || '重置链接无效或已过期，请重新申请');
        setStep('invalid');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function startCooldown() {
    setCooldown(60);
    const iv = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(iv); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  const resendSms = async () => {
    if (smsCooldown > 0) return;
    setLoading(true); setError('');
    try {
      const res = await api.post('/auth/reset-password/send-sms', { token });
      setMasked(res.data.masked_phone || '');
      setSmsSent(true);
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.error || '发送失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!smsCode.trim()) { setError(t('auth.codePlaceholder')); return; }
    if (newPwd.length < 8) { setError(t('auth.newPasswordPlaceholder')); return; }
    if (newPwd !== confirmPwd) { setError(t('auth.confirmPasswordPlaceholder')); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', { token, sms_code: smsCode.trim(), new_password: newPwd });
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || '重置失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'var(--sans)', background: '#F8F5F0', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid ' + BD, padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <div style={{ width: 32, height: 32, borderRadius: 4, background: '#8C1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5E6D0', fontSize: 10, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1 }}>云川<br/>中古</div>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 17, color: GOLD, letterSpacing: 4 }}>云川中古</span>
        </div>
        <span onClick={() => navigate('/login')} style={{ fontSize: 12, color: GOLD, cursor: 'pointer' }}>{t('auth.backToLogin')}</span>
      </header>

      <div style={{ maxWidth: 420, margin: '60px auto', padding: '0 20px' }}>
        <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(26,18,8,.10)' }}>
          {/* Card header */}
          <div style={{ padding: '20px 28px 16px', borderBottom: `2px solid ${RED}`, background: 'linear-gradient(135deg,#F8F5F0,#fff)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 3, color: RED, marginBottom: 6 }}>PASSWORD RESET</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600, color: '#110E0A', letterSpacing: 2 }}>{t('auth.resetPassword')}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{t('auth.newPasswordPlaceholder')}</div>
          </div>

          <div style={{ padding: 28 }}>

            {/* ── loading ── */}
            {step === 'loading' && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: MUTED, fontSize: 13 }}>
                {t('common.loading')}
              </div>
            )}

            {/* ── invalid ── */}
            {step === 'invalid' && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#110E0A', marginBottom: 8 }}>{t('common.failed')}</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.8, marginBottom: 20 }}>
                  {invalidReason}
                </div>
                <button onClick={() => navigate('/forgot-password')} style={{ padding: '10px 28px', background: RED, border: 'none', borderRadius: 2, color: '#fff', fontSize: 13, cursor: 'pointer', marginRight: 12 }}>
                  {t('common.retry')}
                </button>
                <button onClick={() => navigate('/login')} style={{ padding: '10px 28px', background: 'transparent', border: `1px solid ${BD}`, borderRadius: 2, color: MUTED, fontSize: 13, cursor: 'pointer' }}>
                  {t('auth.backToLogin')}
                </button>
              </div>
            )}

            {/* ── sms + new password ── */}
            {step === 'sms' && (
              <>
                {/* Security badge */}
                <div style={{ padding: '10px 14px', background: 'rgba(42,102,69,0.07)', border: '1px solid rgba(42,102,69,0.15)', borderRadius: 2, fontSize: 12, color: GREEN, marginBottom: 20 }}>
                  🔒 重置链接已验证，还需验证注册手机号以完成身份确认
                </div>

                {error && (
                  <div style={{ padding: '10px 14px', borderRadius: 2, marginBottom: 16, background: 'rgba(140,31,31,0.07)', border: '1px solid rgba(140,31,31,0.2)', color: RED, fontSize: 12 }}>
                    ⚠ {error}
                  </div>
                )}

                {/* SMS code */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                    {t('auth.verifyCode')} <span style={{ color: RED, fontSize: 10 }}>*</span>
                    {maskedPhone && <span style={{ color: '#A8998A', marginLeft: 6 }}>已发送至 {maskedPhone}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text" inputMode="numeric" value={smsCode}
                      onChange={e => { setSmsCode(e.target.value); setError(''); }}
                      onKeyDown={e => e.key === 'Enter' && submit()}
                      placeholder={t('auth.codePlaceholder')}
                      maxLength={6}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      onClick={resendSms}
                      disabled={smsCooldown > 0 || loading}
                      style={{
                        flexShrink: 0, padding: '0 14px', fontSize: 12,
                        background: smsCooldown > 0 ? '#F0EBE3' : '#fff',
                        border: '1px solid ' + BD, borderRadius: 2,
                        color: smsCooldown > 0 ? '#A8998A' : GOLD,
                        cursor: smsCooldown > 0 ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {smsCooldown > 0 ? `${smsCooldown}s` : smsSent ? t('auth.sendCode') : t('auth.sendCode')}
                    </button>
                  </div>
                </div>

                {/* New password */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                    {t('settings.newPassword')} <span style={{ color: RED, fontSize: 10 }}>*</span>
                    <span style={{ color: '#A8998A', marginLeft: 6 }}>至少 8 位</span>
                  </div>
                  <input
                    type="password" value={newPwd}
                    onChange={e => { setNewPwd(e.target.value); setError(''); }}
                    placeholder={t('auth.newPasswordPlaceholder')}
                    style={inputStyle}
                  />
                </div>

                {/* Confirm password */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                    {t('settings.confirmNewPassword')} <span style={{ color: RED, fontSize: 10 }}>*</span>
                  </div>
                  <input
                    type="password" value={confirmPwd}
                    onChange={e => { setConfirm(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && submit()}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    style={inputStyle}
                  />
                </div>

                <button
                  onClick={submit}
                  disabled={loading || !smsCode.trim() || !newPwd || !confirmPwd}
                  style={{
                    width: '100%', padding: 13,
                    background: loading || !smsCode.trim() || !newPwd || !confirmPwd ? '#C0A882' : RED,
                    border: 'none', borderRadius: 2, color: '#fff',
                    fontFamily: 'var(--sans)', fontSize: 14,
                    cursor: loading || !smsCode.trim() || !newPwd || !confirmPwd ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? t('common.loading') : t('auth.resetPassword')}
                </button>
              </>
            )}

            {/* ── done ── */}
            {step === 'done' && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#110E0A', marginBottom: 8 }}>{t('auth.resetSuccess')}</div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.8, marginBottom: 24 }}>
                  {t('auth.resetSuccess')}
                </div>
                <button onClick={() => navigate('/login')} style={{ padding: '10px 28px', background: GOLD, border: 'none', borderRadius: 2, color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                  {t('auth.goLogin')}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
